import { useState, useMemo, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColDef, SelectionChangedEvent, IRowNode } from 'ag-grid-community';
import {
  ArrowLeft,
  Shield,
  Users,
  Package,
  Plus,
  Trash2,
  X,
  Search,
  CalendarDays,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessControl } from '@/contexts/AccessControlContext';
import { supabase } from '@/integrations/supabase/client';
import {
  userAdminService,
  permissionService,
  permissionAdminService,
  moduleAccessService,
  userGroupService,
  userPermissionService,
  groupMemberService,
  groupService,
  profileService,
  type UserProfileRow,
  type UserPermission,
  type UserModuleAccess,
  type LicensedModuleRow,
  type PermissionRow,
  type GroupRow,
  type ServiceContext,
} from '@/services';
import { usePagination } from '@/hooks/usePagination';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { formatDateRange } from '@/lib/daterange';
import { toast } from 'sonner';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return '\u2014';
  }
}


/* ------------------------------------------------------------------ */
/*  Add Permission Drawer                                              */
/* ------------------------------------------------------------------ */

interface AddPermissionDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availablePermissions: PermissionRow[];
  isAdding: string | null;
  onAdd: (permission: PermissionRow) => void;
}

function AddPermissionDrawer({
  open,
  onOpenChange,
  availablePermissions,
  isAdding,
  onAdd,
}: AddPermissionDrawerProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return availablePermissions;
    const q = search.toLowerCase();
    return availablePermissions.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        (p.category?.toLowerCase().includes(q) ?? false)
    );
  }, [availablePermissions, search]);

  const grouped = useMemo(() => {
    return filtered.reduce<Record<string, PermissionRow[]>>((acc, p) => {
      const cat = p.category || 'Uncategorized';
      (acc[cat] ??= []).push(p);
      return acc;
    }, {});
  }, [filtered]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[400px] sm:w-[440px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Add Permission</SheetTitle>
          <SheetDescription>
            Click a permission to assign it directly to this user.
          </SheetDescription>
        </SheetHeader>

        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter permissions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={search ? 'pl-9 pr-8' : 'pl-9'}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <ScrollArea className="flex-1 mt-4 -mx-6 px-6">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {availablePermissions.length === 0
                ? 'All permissions are already assigned.'
                : 'No matching permissions found.'}
            </p>
          ) : (
            <div className="space-y-4 pb-4">
              {Object.entries(grouped)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, perms]) => (
                  <div key={category}>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      {category}
                    </h4>
                    <div className="space-y-1">
                      {perms.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          disabled={isAdding === p.id}
                          onClick={() => onAdd(p)}
                          className="w-full flex items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors text-left disabled:opacity-50"
                        >
                          <div className="min-w-0">
                            <span className="font-medium">{p.name}</span>
                            <span className="ml-2 text-xs text-muted-foreground font-mono">
                              {p.code}
                            </span>
                            {p.description && (
                              <p className="text-xs text-muted-foreground truncate">
                                {p.description}
                              </p>
                            )}
                          </div>
                          <Plus className="h-4 w-4 shrink-0 ml-2 text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ */
/*  Permissions Tab                                                    */
/* ------------------------------------------------------------------ */

interface PermissionsTabProps {
  profileId: string;
  companyId: string;
  context: ServiceContext;
}

function PermissionsTab({ profileId, companyId, context }: PermissionsTabProps) {
  const queryClient = useQueryClient();
  const { invalidateCache: invalidateAccessCache } = useAccessControl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<UserPermission[]>([]);
  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    pageSizeOptions,
  } = usePagination({ defaultPageSize: 25, storageKey: 'user-detail-perms' });

  // Effective permissions (from view)
  const { data: rawPermissions, isLoading: permLoading } = useQuery({
    queryKey: ['user-permissions', profileId, companyId],
    queryFn: async () => {
      const r = await permissionService.getUserPermissions(profileId, companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 30_000,
  });

  // All company permissions (for the drawer)
  const { data: allPermissions } = useQuery({
    queryKey: ['company-permissions', companyId],
    queryFn: async () => {
      const r = await permissionAdminService.list(companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const permissions = rawPermissions ?? [];

  // Client-side search
  const filteredPerms = useMemo(() => {
    if (!debouncedSearch) return permissions;
    const q = debouncedSearch.toLowerCase();
    return permissions.filter(
      (p) =>
        p.permission_name.toLowerCase().includes(q) ||
        p.permission_code.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
    );
  }, [permissions, debouncedSearch]);

  // Client-side pagination
  const paginatedPerms = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredPerms.slice(start, start + pageSize);
  }, [filteredPerms, pageIndex, pageSize]);

  // Available permissions for drawer (not already assigned directly)
  const availablePermissions = useMemo(() => {
    if (!allPermissions) return [];
    const directIds = new Set(
      permissions.filter((p) => p.grant_source === 'DIRECT').map((p) => p.permission_id)
    );
    return allPermissions.filter((p) => p.is_active && !directIds.has(p.id));
  }, [allPermissions, permissions]);

  const groupTooltip =
    'This permission is assigned via group assignment. Remove the user from the group to remove this permission from the user';

  const columnDefs = useMemo<ColDef<UserPermission>[]>(
    () => [
      {
        headerName: 'Name',
        field: 'permission_name',
        flex: 1,
        minWidth: 180,
        tooltipValueGetter: (params) =>
          params.data?.grant_source === 'GROUP' ? groupTooltip : undefined,
      },
      {
        headerName: 'Code',
        field: 'permission_code',
        width: 180,
        cellClass: 'font-mono text-xs',
        tooltipValueGetter: (params) =>
          params.data?.grant_source === 'GROUP' ? groupTooltip : undefined,
      },
      {
        headerName: 'Category',
        field: 'category',
        width: 150,
        rowGroup: true,
        hide: true,
      },
      {
        headerName: 'Grant Source',
        field: 'grant_source',
        width: 160,
        tooltipValueGetter: (params) =>
          params.data?.grant_source === 'GROUP' ? groupTooltip : undefined,
        cellRenderer: (params: { data: UserPermission | undefined }) => {
          if (!params.data) return null;
          const { grant_source, group_name } = params.data;
          return (
            <div className="flex items-center h-full">
              <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
                {grant_source === 'DIRECT' ? 'Direct' : group_name}
              </Badge>
            </div>
          );
        },
      },
    ],
    []
  );

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<UserPermission>) => {
      setSelectedRows(event.api.getSelectedRows());
    },
    []
  );

  const handleAddPermission = useCallback(
    async (permission: PermissionRow) => {
      setIsAdding(permission.id);
      try {
        const result = await userPermissionService.assign(
          profileId,
          permission.id,
          context
        );
        if (result.error) {
          toast.error(`Failed to add: ${result.error.message}`);
        } else {
          toast.success(`Added "${permission.name}"`);
          queryClient.invalidateQueries({ queryKey: ['user-permissions', profileId, companyId] });
          invalidateAccessCache();
        }
      } finally {
        setIsAdding(null);
      }
    },
    [profileId, companyId, context, queryClient, invalidateAccessCache]
  );

  const handleDeleteSelected = useCallback(async () => {
    if (selectedRows.length === 0) return;

    setIsDeleting(true);
    try {
      const ids = selectedRows.map((r) => r.permission_id);
      const result = await userPermissionService.removeBulk(profileId, ids, companyId);
      if (result.error) {
        toast.error(`Failed to remove: ${result.error.message}`);
      } else {
        toast.success(`Removed ${selectedRows.length} permission(s)`);
        setSelectedRows([]);
        queryClient.invalidateQueries({ queryKey: ['user-permissions', profileId, companyId] });
        invalidateAccessCache();
      }
    } finally {
      setIsDeleting(false);
    }
  }, [selectedRows, profileId, companyId, queryClient, invalidateAccessCache]);

  return (
    <>
      <AGDataGrid<UserPermission>
        rowData={paginatedPerms}
        columnDefs={columnDefs}
        totalCount={filteredPerms.length}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search permissions..."
        isLoading={permLoading}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        onSelectionChanged={handleSelectionChanged}
        rowSelection={{
          mode: 'multiRow' as const,
          checkboxes: true,
          headerCheckbox: true,
          isRowSelectable: (node: IRowNode<UserPermission>) =>
            !!node.data && node.data.grant_source === 'DIRECT',
        }}
        groupDefaultExpanded={-1}
        autoGroupColumnDef={{ headerName: 'Category', minWidth: 200 }}
        height="calc(100vh - 420px)"
        toolbarActions={
          <div className="flex items-center gap-2">
            {selectedRows.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteSelected}
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove ({selectedRows.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Permission
            </Button>
          </div>
        }
      />

      <AddPermissionDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        availablePermissions={availablePermissions}
        isAdding={isAdding}
        onAdd={handleAddPermission}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Add Group Drawer                                                   */
/* ------------------------------------------------------------------ */

interface AddGroupDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableGroups: GroupRow[];
  isAdding: string | null;
  onAdd: (group: GroupRow) => void;
}

function AddGroupDrawer({
  open,
  onOpenChange,
  availableGroups,
  isAdding,
  onAdd,
}: AddGroupDrawerProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return availableGroups;
    const q = search.toLowerCase();
    return availableGroups.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        (g.description?.toLowerCase().includes(q) ?? false)
    );
  }, [availableGroups, search]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[400px] sm:w-[440px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Add Group</SheetTitle>
          <SheetDescription>
            Click a group to add this user as a member.
          </SheetDescription>
        </SheetHeader>

        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={search ? 'pl-9 pr-8' : 'pl-9'}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <ScrollArea className="flex-1 mt-4 -mx-6 px-6">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {availableGroups.length === 0
                ? 'User is already a member of all groups.'
                : 'No matching groups found.'}
            </p>
          ) : (
            <div className="space-y-1 pb-4">
              {filtered.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  disabled={isAdding === g.id}
                  onClick={() => onAdd(g)}
                  className="w-full flex items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors text-left disabled:opacity-50"
                >
                  <div className="min-w-0">
                    <span className="font-medium">{g.name}</span>
                    {g.description && (
                      <p className="text-xs text-muted-foreground truncate">
                        {g.description}
                      </p>
                    )}
                  </div>
                  <Plus className="h-4 w-4 shrink-0 ml-2 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ */
/*  Groups Tab                                                         */
/* ------------------------------------------------------------------ */

interface GroupsTabProps {
  profileId: string;
  companyId: string;
  context: ServiceContext;
}

function GroupsTab({ profileId, companyId, context }: GroupsTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { invalidateCache: invalidateAccessCache } = useAccessControl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [removingGroup, setRemovingGroup] = useState<{
    group_id: string;
    group_name: string;
  } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const { data: groups, isLoading } = useQuery({
    queryKey: ['user-groups', profileId, companyId],
    queryFn: async () => {
      const r = await userGroupService.getUserGroups(profileId, companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  // All company groups (for the drawer)
  const { data: allGroups } = useQuery({
    queryKey: ['company-groups', companyId],
    queryFn: async () => {
      const r = await groupService.list(companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  // Available groups for drawer (active, not already a member)
  const availableGroups = useMemo(() => {
    if (!allGroups) return [];
    const memberIds = new Set((groups ?? []).map((g) => g.group_id));
    return allGroups.filter((g) => g.is_active && !memberIds.has(g.id));
  }, [allGroups, groups]);

  const invalidateGroupQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['user-groups', profileId, companyId] });
    queryClient.invalidateQueries({ queryKey: ['user-permissions', profileId, companyId] });
    queryClient.invalidateQueries({ queryKey: ['user-modules', profileId, companyId] });
    invalidateAccessCache();
  }, [queryClient, profileId, companyId, invalidateAccessCache]);

  const handleAddGroup = useCallback(
    async (group: GroupRow) => {
      setIsAdding(group.id);
      try {
        const result = await groupMemberService.addMember(group.id, profileId, context);
        if (result.error) {
          toast.error(`Failed to add to group: ${result.error.message}`);
        } else {
          toast.success(`Added to "${group.name}"`);
          invalidateGroupQueries();
        }
      } finally {
        setIsAdding(null);
      }
    },
    [profileId, context, invalidateGroupQueries]
  );

  const handleRemove = useCallback(async () => {
    if (!removingGroup) return;
    setIsRemoving(true);
    try {
      const result = await groupMemberService.removeBulkMembers(
        removingGroup.group_id,
        [profileId],
        companyId
      );
      if (result.error) {
        toast.error(`Failed to remove from group: ${result.error.message}`);
      } else {
        toast.success(`Removed from "${removingGroup.group_name}"`);
        invalidateGroupQueries();
      }
    } finally {
      setIsRemoving(false);
      setRemovingGroup(null);
    }
  }, [removingGroup, profileId, companyId, invalidateGroupQueries]);

  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="p-4">
        <div className="flex justify-end mb-4">
          <Button variant="outline" size="sm" onClick={() => setDrawerOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Group
          </Button>
        </div>

        {!groups?.length ? (
          <p className="text-sm text-muted-foreground">Not a member of any groups.</p>
        ) : (
          <div className="space-y-2">
            {groups.map((g) => (
              <div
                key={g.group_id}
                className="flex items-center justify-between rounded-md border px-4 py-3"
              >
                <div>
                  <button
                    type="button"
                    onClick={() => navigate(`/security/groups/${g.group_id}`)}
                    className="font-medium text-sm text-primary hover:underline text-left"
                  >
                    {g.group_name}
                  </button>
                  {g.group_description && (
                    <p className="text-xs text-muted-foreground">{g.group_description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={g.is_active ? 'secondary' : 'outline'}>
                    {g.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => setRemovingGroup({ group_id: g.group_id, group_name: g.group_name })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AddGroupDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        availableGroups={availableGroups}
        isAdding={isAdding}
        onAdd={handleAddGroup}
      />

      <AlertDialog open={!!removingGroup} onOpenChange={(open) => !open && setRemovingGroup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this user from &ldquo;{removingGroup?.group_name}&rdquo;?
              Any permissions or module access inherited from this group will be revoked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Add License Drawer                                                 */
/* ------------------------------------------------------------------ */

interface AddLicenseDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableLicenses: LicensedModuleRow[];
  isAdding: string | null;
  onAdd: (license: LicensedModuleRow) => void;
}

function AddLicenseDrawer({
  open,
  onOpenChange,
  availableLicenses,
  isAdding,
  onAdd,
}: AddLicenseDrawerProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return availableLicenses;
    const q = search.toLowerCase();
    return availableLicenses.filter(
      (l) =>
        l.module_name.toLowerCase().includes(q) ||
        l.module_code.toLowerCase().includes(q) ||
        (l.module_description?.toLowerCase().includes(q) ?? false)
    );
  }, [availableLicenses, search]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[400px] sm:w-[440px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Add License</SheetTitle>
          <SheetDescription>
            Click a module to assign the license directly to this user.
          </SheetDescription>
        </SheetHeader>

        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter modules..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={search ? 'pl-9 pr-8' : 'pl-9'}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <ScrollArea className="flex-1 mt-4 -mx-6 px-6">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {availableLicenses.length === 0
                ? 'All licensed modules are already assigned.'
                : 'No matching modules found.'}
            </p>
          ) : (
            <div className="space-y-1 pb-4">
              {filtered.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  disabled={isAdding === l.id}
                  onClick={() => onAdd(l)}
                  className="w-full flex items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors text-left disabled:opacity-50"
                >
                  <div className="min-w-0">
                    <span className="font-medium">{l.module_name}</span>
                    <span className="ml-2 text-xs text-muted-foreground font-mono">
                      {l.module_code}
                    </span>
                    {l.module_description && (
                      <p className="text-xs text-muted-foreground truncate">
                        {l.module_description}
                      </p>
                    )}
                    {l.effective && (
                      <p className="text-xs text-muted-foreground">
                        {formatDateRange(l.effective)}
                      </p>
                    )}
                  </div>
                  <Plus className="h-4 w-4 shrink-0 ml-2 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ */
/*  Modules Tab                                                        */
/* ------------------------------------------------------------------ */

interface ConsolidatedModule {
  module_code: string;
  module_name: string;
  module_description: string | null;
  license_effective: string | null;
  licensed_module_id: string;
  sources: Array<{
    type: 'DIRECT' | 'GROUP';
    group_id: string | null;
    group_name: string | null;
  }>;
  hasDirect: boolean;
  hasGroupSources: boolean;
}

function consolidateModules(rows: UserModuleAccess[]): ConsolidatedModule[] {
  const map = new Map<string, ConsolidatedModule>();

  for (const row of rows) {
    let entry = map.get(row.module_code);
    if (!entry) {
      entry = {
        module_code: row.module_code,
        module_name: row.module_name,
        module_description: row.module_description,
        license_effective: row.license_effective,
        licensed_module_id: row.licensed_module_id,
        sources: [],
        hasDirect: false,
        hasGroupSources: false,
      };
      map.set(row.module_code, entry);
    }
    entry.sources.push({
      type: row.access_source,
      group_id: row.group_id,
      group_name: row.group_name,
    });
    if (row.access_source === 'DIRECT') entry.hasDirect = true;
    if (row.access_source === 'GROUP') entry.hasGroupSources = true;
  }

  return Array.from(map.values()).sort((a, b) =>
    a.module_name.localeCompare(b.module_name)
  );
}

interface ModulesTabProps {
  profileId: string;
  companyId: string;
  context: ServiceContext;
}

function ModulesTab({ profileId, companyId, context }: ModulesTabProps) {
  const queryClient = useQueryClient();
  const { invalidateCache: invalidateAccessCache } = useAccessControl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [removingGroup, setRemovingGroup] = useState<{
    group_id: string;
    group_name: string;
    module_name: string;
  } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [unassigningModule, setUnassigningModule] = useState<ConsolidatedModule | null>(null);
  const [isUnassigning, setIsUnassigning] = useState(false);

  const { data: modules, isLoading } = useQuery({
    queryKey: ['user-modules', profileId, companyId],
    queryFn: async () => {
      const r = await moduleAccessService.getUserModules(profileId, companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const consolidated = useMemo(
    () => consolidateModules(modules ?? []),
    [modules]
  );

  // All licensed modules for the company (for the drawer)
  const { data: allLicensedModules } = useQuery({
    queryKey: ['company-licensed-modules', companyId],
    queryFn: async () => {
      const r = await moduleAccessService.listLicensedModules(companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  // Available licenses for drawer (not already directly assigned)
  const availableLicenses = useMemo(() => {
    if (!allLicensedModules) return [];
    const assignedIds = new Set(
      (modules ?? [])
        .filter((m) => m.access_source === 'DIRECT')
        .map((m) => m.licensed_module_id)
    );
    return allLicensedModules.filter((l) => !assignedIds.has(l.id));
  }, [allLicensedModules, modules]);

  // Licensed features for the company, grouped by module code
  const { data: licensedFeatures } = useQuery({
    queryKey: ['company-licensed-features', companyId],
    queryFn: async () => {
      const r = await moduleAccessService.getCompanyLicensedFeatures(companyId);
      if (r.error) throw r.error;
      return r.data;
    },
  });

  const featuresByModule = useMemo(() => {
    const map = new Map<string, typeof licensedFeatures>();
    for (const f of licensedFeatures ?? []) {
      const list = map.get(f.module_code) ?? [];
      list.push(f);
      map.set(f.module_code, list);
    }
    return map;
  }, [licensedFeatures]);

  const invalidateModuleQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['user-modules', profileId, companyId] });
    queryClient.invalidateQueries({ queryKey: ['user-groups', profileId, companyId] });
    queryClient.invalidateQueries({ queryKey: ['user-permissions', profileId, companyId] });
    invalidateAccessCache();
  }, [queryClient, profileId, companyId, invalidateAccessCache]);

  const handleRemoveFromGroup = useCallback(async () => {
    if (!removingGroup) return;
    setIsRemoving(true);
    try {
      const result = await groupMemberService.removeBulkMembers(
        removingGroup.group_id,
        [profileId],
        companyId
      );
      if (result.error) {
        toast.error(`Failed to remove from group: ${result.error.message}`);
      } else {
        toast.success(`Removed from "${removingGroup.group_name}"`);
        invalidateModuleQueries();
      }
    } finally {
      setIsRemoving(false);
      setRemovingGroup(null);
    }
  }, [removingGroup, profileId, companyId, invalidateModuleQueries]);

  const handleUnassign = useCallback(async () => {
    if (!unassigningModule) return;
    setIsUnassigning(true);
    try {
      const result = await moduleAccessService.unassignDirectModule(
        profileId,
        unassigningModule.licensed_module_id,
        companyId
      );
      if (result.error) {
        toast.error(`Failed to unassign: ${result.error.message}`);
      } else {
        toast.success(`Unassigned "${unassigningModule.module_name}"`);
        queryClient.invalidateQueries({ queryKey: ['user-modules', profileId, companyId] });
        invalidateAccessCache();
      }
    } finally {
      setIsUnassigning(false);
      setUnassigningModule(null);
    }
  }, [unassigningModule, profileId, companyId, queryClient, invalidateAccessCache]);

  const handleAddLicense = useCallback(
    async (license: LicensedModuleRow) => {
      setIsAdding(license.id);
      try {
        const result = await moduleAccessService.assignDirectModule(
          profileId,
          license.id,
          companyId
        );
        if (result.error) {
          toast.error(`Failed to assign: ${result.error.message}`);
        } else {
          toast.success(`Assigned "${license.module_name}"`);
          queryClient.invalidateQueries({ queryKey: ['user-modules', profileId, companyId] });
          invalidateAccessCache();
        }
      } finally {
        setIsAdding(null);
      }
    },
    [profileId, companyId, queryClient, invalidateAccessCache]
  );

  if (isLoading) {
    return (
      <div className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-48 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  const addButton = (
    <div className="flex justify-end p-4 pb-0">
      <Button variant="outline" size="sm" onClick={() => setDrawerOpen(true)}>
        <Plus className="h-4 w-4 mr-2" />
        Add License
      </Button>
    </div>
  );

  if (!consolidated.length) {
    return (
      <>
        {addButton}
        <p className="text-sm text-muted-foreground p-4">No module access assigned.</p>
        <AddLicenseDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          availableLicenses={availableLicenses}
          isAdding={isAdding}
          onAdd={handleAddLicense}
        />
      </>
    );
  }

  return (
    <>
      {addButton}
      <div className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
        {consolidated.map((mod) => (
          <Card key={mod.module_code}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{mod.module_name}</CardTitle>
                <span className="text-xs text-muted-foreground font-mono">{mod.module_code}</span>
              </div>
              {mod.module_description && (
                <CardDescription>{mod.module_description}</CardDescription>
              )}
            </CardHeader>

            <CardContent className="space-y-3">
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <CalendarDays className="h-4 w-4 shrink-0" />
                <span>{formatDateRange(mod.license_effective)}</span>
              </div>

              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Access Sources</p>
                <div className="flex flex-wrap gap-1.5">
                  {mod.sources.map((source, idx) =>
                    source.type === 'DIRECT' ? (
                      <Badge
                        key={idx}
                        className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100"
                      >
                        Direct
                      </Badge>
                    ) : (
                      <Tooltip key={idx}>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="secondary"
                            className="group/badge cursor-pointer gap-1 pr-1.5"
                            onClick={() =>
                              setRemovingGroup({
                                group_id: source.group_id!,
                                group_name: source.group_name!,
                                module_name: mod.module_name,
                              })
                            }
                          >
                            {source.group_name}
                            <X className="h-3 w-3 opacity-0 group-hover/badge:opacity-100 text-destructive transition-opacity" />
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>This user must be removed from the group to remove this license</p>
                        </TooltipContent>
                      </Tooltip>
                    )
                  )}
                </div>
              </div>

              {(featuresByModule.get(mod.module_code) ?? []).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">Licensed Features</p>
                  <div className="divide-y divide-border">
                    {featuresByModule.get(mod.module_code)!.map(f => (
                      <div key={f.feature_code} className="py-1.5 first:pt-0">
                        <p className="text-sm font-medium">{f.feature_name}</p>
                        {f.feature_description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{f.feature_description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>

            {mod.hasDirect && (
              <CardFooter className="flex-col items-start gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={mod.hasGroupSources}
                  onClick={() => setUnassigningModule(mod)}
                  className={!mod.hasGroupSources ? 'text-destructive hover:text-destructive' : ''}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Unassign License
                </Button>
                {mod.hasGroupSources && (
                  <p className="text-xs text-muted-foreground">
                    Remove user from groups that grant this license first
                  </p>
                )}
              </CardFooter>
            )}
          </Card>
        ))}
      </div>

      {/* Group removal confirm dialog */}
      <AlertDialog open={!!removingGroup} onOpenChange={(open) => !open && setRemovingGroup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from group</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the user from &ldquo;{removingGroup?.group_name}&rdquo;.
              Any permissions or module access inherited from this group will be revoked,
              including access to &ldquo;{removingGroup?.module_name}&rdquo;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveFromGroup}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unassign license confirm dialog */}
      <AlertDialog open={!!unassigningModule} onOpenChange={(open) => !open && setUnassigningModule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unassign module license</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the direct license assignment for
              &ldquo;{unassigningModule?.module_name}&rdquo;? This user will no longer have
              access to this module.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnassigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnassign}
              disabled={isUnassigning}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isUnassigning ? 'Removing...' : 'Unassign'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddLicenseDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        availableLicenses={availableLicenses}
        isAdding={isAdding}
        onAdd={handleAddLicense}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                     */
/* ------------------------------------------------------------------ */

export default function SecurityUserDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  // Get companyId from the authenticated user's profile
  const { data: companyId } = useQuery({
    queryKey: ['my-company-id', authUser?.id],
    queryFn: async () => {
      const { data: profile } = await profileService.get(authUser!.id);
      return profile?.company_id ?? null;
    },
    enabled: !!authUser,
    staleTime: 10 * 60 * 1000,
  });

  // Fetch user data (with route state as placeholder for instant display)
  const routeUser = (location.state as { user?: UserProfileRow } | null)?.user ?? undefined;

  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: ['user-detail', id, companyId],
    queryFn: async () => {
      const result = await userAdminService.listUsers(companyId!);
      if (result.error) throw result.error;
      const found = result.data.find((u) => u.id === id);
      if (!found) throw new Error('User not found');
      return found;
    },
    enabled: !!id && !!companyId,
    placeholderData: routeUser?.id === id ? routeUser : undefined,
  });

  const serviceContext = useMemo<ServiceContext>(
    () => ({ companyId: companyId!, userId: authUser?.id }),
    [companyId, authUser?.id]
  );

  const user = userData;
  const isDeleted = !!user?.deleted_at;

  const { data: signedAvatarUrl } = useQuery({
    queryKey: ['avatar-url', user?.id, user?.avatar_url],
    queryFn: async () => {
      const { data } = await supabase.storage
        .from('avatars')
        .createSignedUrl(user!.avatar_url!, 3600);
      return data?.signedUrl ?? null;
    },
    enabled: !!user?.avatar_url,
    staleTime: 30 * 60 * 1000,
  });

  if (!id) {
    return <p className="p-4 text-muted-foreground">No user ID provided.</p>;
  }

  if (isUserLoading && !user) {
    return (
      <div className="animate-slide-up space-y-6 p-2">
        <Skeleton className="h-8 w-48" />
        <div className="flex items-start gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/security/users')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </Button>
        <p className="text-muted-foreground mt-4">User not found.</p>
      </div>
    );
  }

  return (
    <div className="animate-slide-up space-y-6">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/security/users')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Users
      </Button>

      {/* Profile header */}
      <div className="flex items-start gap-4">
        <Avatar className="h-16 w-16">
          {signedAvatarUrl && <AvatarImage src={signedAvatarUrl} alt={user.full_name ?? ''} />}
          <AvatarFallback className="text-xl bg-primary/10 text-primary">
            {getInitials(user.full_name)}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">{user.full_name || 'Unnamed User'}</h1>
            {isDeleted && <Badge variant="destructive">Deleted</Badge>}
          </div>
          <div className="mt-1 space-y-0.5 text-sm text-muted-foreground">
            {user.email && <p>{user.email}</p>}
            {user.phone && <p>{user.phone}</p>}
            <p className="text-xs">Last login: {formatDateTime(user.last_sign_in_at)}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      {companyId && (
        <Tabs defaultValue="permissions" className="w-full">
          <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-4">
            <TabsTrigger
              value="permissions"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 gap-1.5"
            >
              <Shield className="h-4 w-4" />
              Permissions
            </TabsTrigger>
            <TabsTrigger
              value="groups"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 gap-1.5"
            >
              <Users className="h-4 w-4" />
              Groups
            </TabsTrigger>
            <TabsTrigger
              value="modules"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 gap-1.5"
            >
              <Package className="h-4 w-4" />
              Modules
            </TabsTrigger>
          </TabsList>

          <TabsContent value="permissions">
            <PermissionsTab profileId={id} companyId={companyId} context={serviceContext} />
          </TabsContent>

          <TabsContent value="groups">
            <GroupsTab profileId={id} companyId={companyId} context={serviceContext} />
          </TabsContent>

          <TabsContent value="modules">
            <ModulesTab profileId={id} companyId={companyId} context={serviceContext} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
